/** Exact decimal-string arithmetic on bigint units. Never use floats for money. */

const AMOUNT_RE = /^\d+(\.\d+)?$/;

export function isAmount(value: string): boolean {
  return AMOUNT_RE.test(value);
}

/** "1.5", 6 -> 1500000n. Extra fractional digits are truncated (rounded down). */
export function toUnits(amount: string, decimals: number): bigint {
  if (!isAmount(amount)) throw new Error(`invalid amount: ${amount}`);
  const [whole = '0', frac = ''] = amount.split('.');
  const padded = (frac + '0'.repeat(decimals)).slice(0, decimals);
  return BigInt(whole) * 10n ** BigInt(decimals) + BigInt(padded || '0');
}

/** 1500000n, 6 -> "1.5" */
export function fromUnits(units: bigint, decimals: number): string {
  const neg = units < 0n;
  const abs = neg ? -units : units;
  const base = 10n ** BigInt(decimals);
  const whole = abs / base;
  const frac = (abs % base).toString().padStart(decimals, '0').replace(/0+$/, '');
  return `${neg ? '-' : ''}${whole}${frac ? `.${frac}` : ''}`;
}

/** Normalise any decimal string to at most `decimals` places, rounding down. */
export function truncate(amount: string, decimals: number): string {
  const neg = amount.startsWith('-');
  const abs = neg ? amount.slice(1) : amount;
  const out = fromUnits(toUnits(abs, decimals), decimals);
  return neg && out !== '0' ? `-${out}` : out;
}

export function compareAmounts(a: string, b: string, decimals = 18): number {
  const x = toUnits(a, decimals);
  const y = toUnits(b, decimals);
  return x === y ? 0 : x > y ? 1 : -1;
}

export function addAmounts(a: string, b: string, decimals: number): string {
  return fromUnits(toUnits(a, decimals) + toUnits(b, decimals), decimals);
}

export function subAmounts(a: string, b: string, decimals: number): string {
  return fromUnits(toUnits(a, decimals) - toUnits(b, decimals), decimals);
}
